import { useEffect, useRef, useState } from 'react';
import { Button } from '../Utility/Button';
import { toast } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import Select from '../Utility/FormElements/Select';
import { setModal } from '../../Store/Actions/modalActions';
import { useNavigate } from 'react-router-dom';
import { setRun, setStartTour, setStepIndex } from '../../Store/Actions/joyrideActions';
import requestClient from '../Utility/RequestClient';
import { setChatlist } from '../../Store/Actions/chatlistActions';
import { setSelectedLanguage } from '../../Store/Actions/i18nActions';
import Input from '../Utility/FormElements/Input';
import { setUserkey } from '../../Store/Actions/userSettingsActions';
import AdminContainer from '../Utility/AdminContainer';
import { setChatlistClear } from '../../Store/Actions/environmentActions';

function UserSettings() {
    const dispatch = useDispatch();
    const i18n = useSelector((state: any) => state.i18n.i18n);
    const selectedLanguage = useSelector((state: any) => state.i18n.selectedLanguage);
    const userSettings = useSelector((state: any) => state.userSettings);
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordRepeat, setNewPasswordRepeat] = useState('');
    const [tmpSelectedLanguage, setTmpSelectedLanguage] = useState(selectedLanguage);
    const requestClientRef = useRef(requestClient);
    const navigate = useNavigate();

    useEffect(() => {
        setTmpSelectedLanguage(selectedLanguage);
    }, [selectedLanguage]);

    useEffect(() => {
        const fetchChats = async () => {
            requestClientRef.current.get(`/chat/list`).then((response: any) => {
                dispatch(setChatlist(response.data.data));
            });
        };

        fetchChats();
    }, [dispatch]);

    const handleUpdateUserLanguage = async () => {
        const data = new URLSearchParams();
        data.append('language', tmpSelectedLanguage);

        requestClient.post(`/settings/updateusersettings`, data).then((response: any) => {
            dispatch(setSelectedLanguage(tmpSelectedLanguage));
            toast.success(i18n.translate({ id: 'toast.success', sourceName: 'UserSettings' }));
        });
    };

    const handleUpdateUserPassword = async () => {
        if (newPassword !== newPasswordRepeat) {
            setNewPassword('');
            setNewPasswordRepeat('');
            toast.error(i18n.translate({ id: 'toast.passwords.notequal', sourceName: 'UserSettings' }));
            return;
        }

        let newUserKey = '';
        const data = new URLSearchParams();
        const hash: (content: string) => Promise<string> = async (content: string) => {
            const encoder = new TextEncoder();
            const data2 = encoder.encode(content);
            const hash2 = await crypto.subtle.digest('SHA-256', data2);
            return Array.from(new Uint8Array(hash2))
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('');
        };
        data.append('passwordHash', await hash(newPassword));
        newUserKey = await hash(newPassword + userSettings.identifier + 'kj25n4br3vqbewnkj');
        data.append('newUserKey', newUserKey);

        requestClient.post(`/settings/updateusersettings`, data).then((response: any) => {
            if (newUserKey !== '') {
                dispatch(setUserkey(newUserKey));
                document.cookie = `userkey=${newUserKey}; path=/`;
                localStorage.setItem('userPassword', newPassword);
            }
            setNewPassword('');
            setNewPasswordRepeat('');

            toast.success(i18n.translate({ id: 'toast.success', sourceName: 'UserSettings' }));
        });
    };

    const handleLanguageChange = (value: string) => {
        setTmpSelectedLanguage(value);
    };

    const removeAllChats = () => {
        dispatch(
            setModal({
                state: 'open',
                title: i18n.translate({ id: 'chatsDelete.title', sourceName: 'UserSettings' }),
                content: i18n.translate({ id: 'chatsDelete.text', sourceName: 'UserSettings' }),
                modalSize: 'small',
                buttonLeftText: i18n.translate({ id: 'cancelConfirmDialog.button', sourceName: 'Main' }),
                buttonRightText: i18n.translate({ id: 'confirmDeleteChat.button', sourceName: 'Main' }),
                closeOnAction: true,
                showCloseButton: false,
                buttonRightAction: confirmDeleteAllChats,
            }),
        );
    };

    const confirmDeleteAllChats = async () => {
        const loading = toast.loading(
            i18n.translate({ id: 'toast.deleteAllChatsLoading', sourceName: 'UserSettings' }),
        );

        await requestClient.delete(`/chat/deleteall`);

        dispatch(setChatlistClear(true));
        dispatch(setChatlist([]));

        toast.dismiss(loading);
        toast.success(i18n.translate({ id: 'toast.allChatsDeleted', sourceName: 'UserSettings' }));
    };

    const startTour = () => {
        dispatch(setStartTour(true));
        dispatch(setRun(true));
        dispatch(setStepIndex(0));
        navigate('/chats');
    };

    return (
        <div className={`admin-container flex relative flex-col`}>
            <AdminContainer>
                <div className="flex flex-wrap gap-4">
                    <div className="box w-full lg:w-[calc(50%-1rem)]">
                        <h4>{i18n.translate({ id: 'language', sourceName: 'UserSettings' })}</h4>
                        <Select value={tmpSelectedLanguage} onChange={handleLanguageChange}>
                            <option value="de">{i18n.translate({ id: 'languages.de' })}</option>
                            <option value="en">{i18n.translate({ id: 'languages.en' })}</option>
                            <option value="fr">{i18n.translate({ id: 'languages.fr' })}</option>
                        </Select>
                        <Button className="mt-3 mb-5" onClick={handleUpdateUserLanguage}>
                            {i18n.translate({ id: 'save' })}
                        </Button>
                    </div>
                    <div className="box w-full lg:w-[calc(50%-1rem)]">
                        <h4>{i18n.translate({ id: 'password.title', sourceName: 'UserSettings' })}</h4>
                        <p>{i18n.translate({ id: 'password.description', sourceName: 'UserSettings' })}</p>
                        <label className="block mt-3 mb-2">
                            {i18n.translate({ id: 'password.first', sourceName: 'UserSettings' })}
                        </label>
                        <Input value={newPassword} onChange={setNewPassword} inputType="password" />
                        <label className="block mt-3 mb-2">
                            {i18n.translate({ id: 'password.repeat', sourceName: 'UserSettings' })}
                        </label>
                        <Input value={newPasswordRepeat} onChange={setNewPasswordRepeat} inputType="password" />
                        <Button className="mt-3" onClick={handleUpdateUserPassword}>
                            {i18n.translate({ id: 'save' })}
                        </Button>
                    </div>
                    <div className="box w-full lg:w-[calc(50%-1rem)]">
                        <h4>{i18n.translate({ id: 'removeAllChats.headline', sourceName: 'UserSettings' })}</h4>
                        <p>{i18n.translate({ id: 'removeAllChats.text', sourceName: 'UserSettings' })}</p>
                        <Button onClick={removeAllChats}>
                            {i18n.translate({ id: 'removeAllChats.button', sourceName: 'UserSettings' })}
                        </Button>
                    </div>
                    <div className="box w-full lg:w-[calc(50%-1rem)]">
                        <h4>{i18n.translate({ id: 'startTour.headline', sourceName: 'UserSettings' })}</h4>
                        <p>{i18n.translate({ id: 'startTour.text', sourceName: 'UserSettings' })}</p>
                        <Button onClick={startTour}>
                            {i18n.translate({ id: 'startTour.button', sourceName: 'UserSettings' })}
                        </Button>
                    </div>
                </div>
            </AdminContainer>
        </div>
    );
}

export default UserSettings;
